import { api } from '@/lib/api/client';
import type {
  HourlyPackage,
  HourlyPackagesResponse,
  CreateHourlyPackageDto,
  UpdateHourlyPackageDto,
  BaseStatus,
  SuccessPackage,
  SuccessPackagesResponse,
  CreateSuccessPackageDto,
  UpdateSuccessPackageDto,
  PackageFilters,
} from '../types';

const HOURLY_PACKAGES_URL = '/hourly-packages';
const SUCCESS_PACKAGES_URL = '/success-packages';

function buildParams(filters?: PackageFilters): Record<string, unknown> {
  const params: Record<string, unknown> = { ...filters };
  if (params['page'] !== undefined && params['limit']) {
    params['offset'] = (params['page'] as number) * (params['limit'] as number);
    delete params['page'];
  }
  Object.keys(params).forEach((key) => {
    if (params[key] == null || params[key] === '') delete params[key];
  });
  return params;
}

// Hourly Packages API
export const hourlyPackagesApi = {
  getAll: async (filters?: PackageFilters): Promise<HourlyPackagesResponse> =>
    api.get<HourlyPackagesResponse>(HOURLY_PACKAGES_URL, { params: buildParams(filters) }),

  getById: async (id: number | string): Promise<HourlyPackage> =>
    api.get<HourlyPackage>(`${HOURLY_PACKAGES_URL}/${id}`),

  create: async (data: CreateHourlyPackageDto): Promise<HourlyPackage> =>
    api.post<HourlyPackage>(HOURLY_PACKAGES_URL, data),

  update: async ({ id, data }: { id: number | string; data: UpdateHourlyPackageDto }): Promise<HourlyPackage> =>
    api.patch<HourlyPackage>(`${HOURLY_PACKAGES_URL}/${id}`, data),

  delete: async (id: number | string): Promise<void> =>
    api.delete(`${HOURLY_PACKAGES_URL}/${id}`),

  changeStatus: async ({ id, status }: { id: number | string; status: BaseStatus }): Promise<HourlyPackage> =>
    api.patch<HourlyPackage>(`${HOURLY_PACKAGES_URL}/change-status/${id}`, { status }),
};

export const hourlyPackagesQueryKeys = {
  all: ['hourly-packages'] as const,
  lists: () => [...hourlyPackagesQueryKeys.all, 'list'] as const,
  list: (filters?: PackageFilters) => [...hourlyPackagesQueryKeys.lists(), filters] as const,
  details: () => [...hourlyPackagesQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...hourlyPackagesQueryKeys.details(), id] as const,
};

// Success Packages API
export const successPackagesApi = {
  getAll: async (filters?: PackageFilters): Promise<SuccessPackagesResponse> =>
    api.get<SuccessPackagesResponse>(SUCCESS_PACKAGES_URL, { params: buildParams(filters) }),

  getById: async (id: number | string): Promise<SuccessPackage> =>
    api.get<SuccessPackage>(`${SUCCESS_PACKAGES_URL}/${id}`),

  create: async (data: CreateSuccessPackageDto): Promise<SuccessPackage> =>
    api.post<SuccessPackage>(SUCCESS_PACKAGES_URL, data),

  update: async ({ id, data }: { id: number | string; data: UpdateSuccessPackageDto }): Promise<SuccessPackage> =>
    api.patch<SuccessPackage>(`${SUCCESS_PACKAGES_URL}/${id}`, data),

  delete: async (id: number | string): Promise<void> =>
    api.delete(`${SUCCESS_PACKAGES_URL}/${id}`),
};

export const successPackagesQueryKeys = {
  all: ['success-packages'] as const,
  lists: () => [...successPackagesQueryKeys.all, 'list'] as const,
  list: (filters?: PackageFilters) => [...successPackagesQueryKeys.lists(), filters] as const,
  details: () => [...successPackagesQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...successPackagesQueryKeys.details(), id] as const,
};
